// Task 3 · "Teken het teken" (opdrachten-browsertechniek.md #20).
// No permissions, no sensors: works everywhere. A $1 recognizer on the device checks the shape.

import { sfx, vibrate } from '../audio.js';
import { backstage } from '../backstage.js';
import { openLightbox } from '../chat.js';
import { recognize, type Point } from '../recognizer.js';
import { openSheet } from '../sheet.js';
import { el, sleep } from '../util.js';

export interface SignResult { method: 'drawn' | 'picked'; attempts: number; score: number; dot: boolean }

const THRESHOLD = 0.78;
const SYMBOLS: Array<[string, string]> = [
  ['cirkel', '<circle cx="50" cy="50" r="38"/>'],
  ['driehoek', '<path d="M50 12 88 80H12Z"/><circle cx="50" cy="60" r="3" fill="currentColor"/>'],
  ['vierkant', '<rect x="16" y="16" width="68" height="68"/>'],
  ['spiraal', '<path d="M50 50c0-4 6-5 8-1 3 6-4 12-10 11-9-2-12-12-8-20 6-11 22-11 29-2 9 12 3 30-11 34-17 5-33-7-34-24"/>'],
];

export async function runSign(): Promise<SignResult> {
  const sheet = openSheet({
    label: 'Opdracht 3 van 3',
    title: 'Teken het teken',
    quote: '“Ellen vergrendelde haar laatste bericht met dat teken. Teken het, in één lijn, zoals zij het met krijt deed.”',
  });

  const canvas = el('canvas', { width: 640, height: 640, 'aria-label': 'Tekenvlak' });
  const guide = el('div.draw__guide', { html: '<svg viewBox="0 0 100 100" width="100%" height="100%" fill="none" stroke="#ece4d3" stroke-width="2" stroke-dasharray="3 3"><path d="M50 16 84 76H16Z"/></svg>' });
  const hint = el('div.draw__hint', {}, 'teken hier');
  const show = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Toon de foto nog eens');
  const cant = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Ik kan niet tekenen');
  const actions = el('div.lock__mode', { style: 'margin-top:14px' }, show, cant);
  sheet.body.append(el('div.draw', {}, canvas, guide, hint), actions);
  show.addEventListener('click', () => openLightbox('/assets/symbol.jpg'));

  const ctx = canvas.getContext('2d')!;
  const scale = () => canvas.width / canvas.getBoundingClientRect().width;
  let stroke: Point[] = [];
  let drawing = false;
  let attempts = 0;
  let dot = false;
  let finished = false;
  let resolveDone!: (r: SignResult) => void;
  const done = new Promise<SignResult>((r) => (resolveDone = r));

  const pos = (e: PointerEvent): Point => {
    const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * scale(), y: (e.clientY - r.top) * scale() };
  };
  const fadeOld = () => {
    ctx.save(); ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = 'rgba(0,0,0,.75)';
    ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.restore();
  };

  canvas.addEventListener('pointerdown', (e) => {
    if (finished) return;
    e.preventDefault();
    drawing = true; hint.hidden = true;
    canvas.setPointerCapture(e.pointerId);
    const p = pos(e);
    stroke = [p];
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(236, 228, 211, .92)'; ctx.lineWidth = 9;
    ctx.shadowColor = 'rgba(255, 220, 190, .35)'; ctx.shadowBlur = 8;
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!drawing) return;
    const p = pos(e);
    stroke.push(p);
    ctx.lineTo(p.x, p.y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  });
  const end = async () => {
    if (!drawing) return;
    drawing = false;
    const xs = stroke.map((p) => p.x), ys = stroke.map((p) => p.y);
    const size = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) / scale();
    if (size < 22) { // a dot
      ctx.beginPath(); ctx.arc(stroke[0].x, stroke[0].y, 9, 0, Math.PI * 2); ctx.fillStyle = 'rgba(236,228,211,.95)'; ctx.fill();
      if (!dot) { dot = true; sheet.feedback('Ook de stip. Goed gezien.', 'good'); sfx.tick(true); }
      return;
    }
    attempts++;
    const m = recognize(stroke);
    backstage.log('ai', `Tekening herkend: ${m.name}`, `$1-herkenner op het toestel: ${m.name} <code>${Math.round(m.score * 100)}%</code> (drempel ${THRESHOLD * 100}%). Geen server, geen AI-dienst.`);
    if (m.name === 'driehoek' && m.score >= THRESHOLD) {
      finished = true;
      sheet.feedback(dot ? 'Het teken klopt. Met de stip.' : 'Het teken klopt.', 'good');
      sfx.success(); vibrate([20, 50, 20, 50, 80]);
      await sleep(1600);
      resolveDone({ method: 'drawn', attempts, score: m.score, dot });
      return;
    }
    sfx.wrong();
    const shape = m.score < 0.6 ? 'Dat lijkt nergens op.' : m.name === 'driehoek' ? 'Bijna. Iets rustiger, met rechte lijnen.' : `Dat lijkt meer op een ${m.name}.`;
    sheet.feedback(`${shape} Kijk nog eens naar de muur.`, 'bad');
    if (attempts >= 3) guide.classList.add('is-visible');
    await sleep(500);
    fadeOld();
  };
  canvas.addEventListener('pointerup', end);
  canvas.addEventListener('pointercancel', end);

  // Alternative: pick the sign instead of drawing it
  cant.addEventListener('click', () => {
    const grid = el('div.symbols', {});
    for (const [name, path] of SYMBOLS) {
      const b = el('button', { type: 'button', 'aria-label': name, html: `<svg viewBox="0 0 100 100" fill="none" stroke="#ece4d3" stroke-width="5" stroke-linejoin="round" style="color:#ece4d3">${path}</svg>` });
      b.addEventListener('click', async () => {
        attempts++;
        if (name === 'driehoek') {
          finished = true; sheet.feedback('Dat is het.', 'good'); sfx.success();
          await sleep(1200);
          resolveDone({ method: 'picked', attempts, score: 1, dot: true });
        } else { sfx.wrong(); sheet.feedback('Nee. Dat stond er niet.', 'bad'); }
      });
      grid.append(b);
    }
    actions.replaceChildren(grid);
    backstage.log('verhaal', 'Alternatief: kiezen in plaats van tekenen', 'Wie niet wil of kan tekenen, kiest het teken uit vier symbolen.');
  });

  const result = await done;
  await sheet.close();
  return result;
}
